import { VersionCostCalculator } from '../../../calculators/costs/VersionCostCalculator'
import { CostFragment } from '../../../generated/graphql'
import { SelectedVersion } from '../../layout/VersionsSelector'
import {
  ISOCostCategory,
  isoCostCategoryToCostTypesMap,
} from './iso-cost-category'
import { Cost, FixedCostRow, GroupedFixedCost } from './cost-table-types'

export const groupCosts = (
  providerNames: Record<string, string>,
  containerNames: Record<string, string>,
  versionIds: Record<string, string>,
  versionNames: Record<string, string>,
  costs: CostFragment[],
  buildingMultipliers: Record<string, number>,
): GroupedFixedCost[] => {
  const groupedCosts = [] as GroupedFixedCost[]

  const versions = Object.entries(versionNames).map(([id, name]) => ({
    id,
    name,
  }))

  for (let i = 0; i < costs.length; i++) {
    const cost = costs[i]

    const versionId = versionIds[cost.id]

    const multiplier = cost.providerId
      ? buildingMultipliers[cost.providerId]
      : 1

    const newCost = {
      id: cost.id,
      versionId,
      value: cost.value,
      multiplier: multiplier ?? 1,
    }
    const containerName = cost.providerId ? containerNames[cost.providerId] : ''

    const providerName = cost.providerId ? providerNames[cost.providerId] : ''

    const existingGroupedCostIndex = groupedCosts.findIndex(
      (groupedCost) => groupedCost.name === containerName,
    )

    if (existingGroupedCostIndex === -1) {
      const newGroupedCost = {
        name: containerName,
        versions,
        costRows: [
          {
            providerName,
            description: cost.description,
            costs: [newCost],
          },
        ],
      }
      groupedCosts.push(newGroupedCost)
    } else {
      const existingCostRowIndex = groupedCosts[
        existingGroupedCostIndex
      ].costRows.findIndex(
        (row) =>
          row.providerName === providerName &&
          row.description === cost.description,
      )

      if (existingCostRowIndex === -1) {
        const costRow = {
          providerName,
          description: cost.description,
          costs: [newCost],
        }
        groupedCosts[existingGroupedCostIndex].costRows.push(costRow)
      } else {
        groupedCosts[existingGroupedCostIndex].costRows[
          existingCostRowIndex
        ].costs.push(newCost)
      }
    }
  }
  return groupedCosts
}

export const getTotalCostsSum = (
  category: Exclude<ISOCostCategory, ISOCostCategory.Energy>,
  version: VersionCostCalculator,
  isContainerSpecific: boolean,
  containerId?: string,
): number | null => {
  const containerNotFound = isContainerSpecific && !containerId
  return containerNotFound
    ? null
    : version.getCumulativeOtherSum(
        isoCostCategoryToCostTypesMap[category],
        containerId,
      )
}

export type CostRowDataForPrefill = {
  providerName: string
  description: string
  cost: Cost
}
export const getPrefilledData = (
  versions: SelectedVersion[],
  costRow: FixedCostRow,
): CostRowDataForPrefill => {
  const { providerName, description, costs } = costRow
  const defaultVersion = versions.find((version) => version.isDefault)
  const cost =
    costs.find((costRowCost) => costRowCost.versionId === defaultVersion?.id) ??
    costs.find(Boolean)

  if (!cost) {
    throw new Error('No costs found as a base for prefill')
  }
  return {
    providerName,
    description,
    cost,
  }
}
